import { soleVariableName } from '@agavi/blocks';
import { Upload, X } from 'lucide-react';
import Papa from 'papaparse';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { parseDateValue, parseNumericValue } from './dataviz-utils';
import { useIsMobile } from './task-content';

import type { ChartPoint, ChartSeries } from '@agavi/blocks';

import Toast from '@/components/toast';
import { Button } from '@/components/ui/button';
import { CheckboxRow } from '@/components/ui/checkbox-row';
import { IconButton } from '@/components/ui/icon-button';
import SelectMenu from '@/components/ui/select-menu';


// 'x' is exclusive (one column at a time — the shared/locked x column every series' rows line up
// against), but 'y' is not: any number of columns can hold it, each becoming its own series sharing
// that single x column — matches the grid editor's own locked-x, per-series-tab model.
type ColumnRole = 'ignore' | 'x' | 'y';

// This chart is a display block, not an analysis tool — capping import size protects the chart's
// per-point SVG rendering, the un-virtualized data table, and (well before either of those) keeps
// sonification meaningful, since its playback is compressed into a fixed ~8s window regardless of
// point count and starts blurring into indistinguishable tones long before technical limits bite.
export const MAX_CSV_SERIES = 8;
export const MAX_CSV_POINTS_PER_SERIES = 250;

/** What kind of data a column actually holds, detected from its non-empty cells. This is noted to
 * the teacher (and drives which roles the mapping dropdown offers — 'y' requires 'numeric') but is
 * NOT editable here: changing how a column is interpreted after import lives in the block editor's
 * grid instead (dataviz-grid.tsx), so re-typing a column never requires deleting data and
 * re-importing. 'empty' locks the column to 'ignore' — there's nothing usable in it for either axis. */
type ColumnDataType = 'numeric' | 'date' | 'text' | 'empty';

function detectColumnType(values: string[]): ColumnDataType {
  const nonEmpty = values.map((v) => v.trim()).filter((v) => v !== '');
  if (nonEmpty.length === 0) return 'empty';
  /*
   * A VARIABLE REFERENCE COUNTS AS NUMERIC, which is what makes this feature usable at all.
   *
   * Nobody types four hundred rows. The authoring path is a spreadsheet — flat at 100, dipping to
   * `{star1_dip}` every thirty rows — exported and pasted here. Without this the column reads as
   * `text`, which cannot be mapped to Y, and the import fails on the one shape it exists for.
   *
   * It is not a column OF variables: the realistic column is mostly numbers with a reference in a
   * few cells, so this is "numeric, some of it named" rather than a fourth column type.
   */
  if (nonEmpty.every((v) => !isNaN(parseNumericValue(v)) || soleVariableName(v) !== null)) return 'numeric';
  if (nonEmpty.every((v) => parseDateValue(v) !== null)) return 'date';
  return 'text';
}

function computeColumnTypes(rows: string[][], firstRowIsHeaders: boolean): ColumnDataType[] {
  const columnCount = rows[0]?.length ?? 0;
  const dataRows = firstRowIsHeaders ? rows.slice(1) : rows;
  return Array.from({ length: columnCount }, (_, colIndex) => detectColumnType(dataRows.map((r) => r[colIndex] ?? '')));
}

interface DataVizCsvImportProps {
  hasExistingData: boolean;
  onImport: (series: ChartSeries[], xAxisType?: 'date', truncation?: { seriesTruncated: boolean; pointsTruncated: boolean }) => void;
  onClose: () => void;
}

export function DataVizCsvImport({ hasExistingData, onImport, onClose }: DataVizCsvImportProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const pasteTabRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const [step, setStep] = useState<'input' | 'mapping'>('input');
  const [inputMode, setInputMode] = useState<'paste' | 'upload'>('paste');
  const [pasteText, setPasteText] = useState('');
  const [parsedRows, setParsedRows] = useState<string[][]>([]);
  const [firstRowIsHeaders, setFirstRowIsHeaders] = useState(true);
  const [columnTypes, setColumnTypes] = useState<ColumnDataType[]>([]);
  const [columnRoles, setColumnRoles] = useState<ColumnRole[]>([]);
  const [toastMsg, setToastMsg] = useState('');
  const [confirmingReplace, setConfirmingReplace] = useState(false);

  // Matches the focus-trap/Escape/restore convention already established in library-modal.tsx —
  // this dialog was missing all three (Tab could escape to the page behind it, Escape did nothing,
  // and closing never returned focus to the "Import CSV" button that opened it).
  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    pasteTabRef.current?.focus();
    return () => previouslyFocusedRef.current?.focus?.();
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const el = overlayRef.current;
    if (!el) return;
    function onTab(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      const focusable = Array.from(
        el!.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'),
      ).filter((n) => !!(n.offsetWidth || n.offsetHeight || n.getClientRects().length));
      if (!focusable.length) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else if (document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    el.addEventListener('keydown', onTab);
    return () => el.removeEventListener('keydown', onTab);
  }, []);

  function parseCsvText(text: string) {
    const result = Papa.parse<string[]>(text, { skipEmptyLines: true });
    if (result.errors.length > 0 || result.data.length === 0) {
      setToastMsg(t('blocks.dataviz.csv.parseError'));
      return;
    }
    const rows = result.data;
    const firstRow = rows[0] ?? [];
    const looksLikeHeader = firstRow.some((cell) => cell.trim() !== '' && isNaN(Number(cell)));
    const types = computeColumnTypes(rows, looksLikeHeader);
    // Best-guess default mapping: first non-empty column -> x, every OTHER numeric column -> a
    // series — a wide CSV like [Date, Series A, Series B] should import as two series out of the
    // box, not leave the teacher promoting each additional numeric column from Ignore by hand.
    // Empty columns can never default to x/y — there's nothing in them to plot.
    const firstXIndex = types.findIndex((ty) => ty !== 'empty');
    const roles: ColumnRole[] = types.map((ty, i) => {
      if (ty === 'empty') return 'ignore';
      if (i === firstXIndex) return 'x';
      if (ty === 'numeric') return 'y';
      return 'ignore';
    });
    setFirstRowIsHeaders(looksLikeHeader);
    setColumnTypes(types);
    setColumnRoles(roles);
    setParsedRows(rows);
    setStep('mapping');
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => parseCsvText(String(reader.result ?? ''));
    reader.onerror = () => setToastMsg(t('blocks.dataviz.csv.parseError'));
    reader.readAsText(file);
  }

  function handleFirstRowHeaderToggle(checked: boolean) {
    setFirstRowIsHeaders(checked);
    const types = computeColumnTypes(parsedRows, checked);
    setColumnTypes(types);
    // Whether row 0 counts as data shifts what each column's cells look like, which can change its
    // detected type — demote any role that's no longer valid for its (possibly new) type rather
    // than resetting the whole mapping the teacher already set up.
    setColumnRoles((prev) => prev.map((r, i) => {
      if (types[i] === 'empty') return 'ignore';
      if (r === 'y' && types[i] !== 'numeric') return 'ignore';
      return r;
    }));
  }

  /** Only one column may hold the 'x' role — picking a new one demotes whichever column previously
   * held it back to 'ignore'. 'y' is not exclusive: any number of columns can hold it at once, each
   * becoming its own series that shares the single 'x' column's values. */
  function handleRoleChange(colIndex: number, role: ColumnRole) {
    setColumnRoles((prev) => prev.map((r, i) => {
      if (i === colIndex) return role;
      if (role === 'x' && r === 'x') return 'ignore';
      return r;
    }));
  }

  function buildSeriesFromMapping(): { series: ChartSeries[]; xAxisType?: 'date'; seriesTruncated: boolean; pointsTruncated: boolean } {
    const headerRow = parsedRows[0] ?? [];
    const dataRows = firstRowIsHeaders ? parsedRows.slice(1) : parsedRows;
    const xColIndex = columnRoles.findIndex((r) => r === 'x');
    const allYColIndices = columnRoles.map((r, i) => (r === 'y' ? i : -1)).filter((i) => i !== -1);
    const yColIndices = allYColIndices.slice(0, MAX_CSV_SERIES);
    if (yColIndices.length === 0) return { series: [], seriesTruncated: false, pointsTruncated: false };

    const xIsDate = xColIndex !== -1 && columnTypes[xColIndex] === 'date';
    let pointsTruncated = false;

    const series: ChartSeries[] = yColIndices.map((yColIndex) => {
      const headerCell = headerRow[yColIndex];
      const label = firstRowIsHeaders && headerCell?.trim() ? headerCell : '';
      const points = dataRows
        .map((row): ChartPoint | null => {
          const xRaw = xColIndex !== -1 ? row[xColIndex] : undefined;
          const yRaw = row[yColIndex];
          if (xRaw === undefined || yRaw === undefined || xRaw.trim() === '' || yRaw.trim() === '') return null;
          /* A reference survives as the text the teacher wrote, resolved when the chart is drawn.
             Everything else in Y still has to be a number. */
          const yRef = soleVariableName(yRaw);
          const y: number | string = yRef ? yRaw.trim() : parseNumericValue(yRaw);
          if (typeof y === 'number' && isNaN(y)) return null;
          /* X DOES NOT TAKE VARIABLES, and this is where that has to be enforced rather than
             assumed: a detected-numeric column can be mapped to X, and `parseNumericValue` returns
             NaN for `{dip}`, so without this the reference would fall through to the category-label
             branch below and print literal braces along an axis. Dropped instead — the same thing
             this importer already does with any row it cannot read. */
          if (soleVariableName(xRaw) !== null) return null;
          if (xIsDate) {
            const xMs = parseDateValue(xRaw);
            if (xMs === null) return null;
            return { x: xMs, y };
          }
          const xNum = parseNumericValue(xRaw);
          // A category (non-numeric, non-date) x column falls through to a plain string label.
          return { x: isNaN(xNum) ? xRaw.trim() : xNum, y };
        })
        .filter((p): p is ChartPoint => p !== null);
      if (points.length > MAX_CSV_POINTS_PER_SERIES) pointsTruncated = true;
      return { id: crypto.randomUUID(), label, points: points.slice(0, MAX_CSV_POINTS_PER_SERIES) };
    });

    return { series, xAxisType: xIsDate ? 'date' : undefined, seriesTruncated: allYColIndices.length > MAX_CSV_SERIES, pointsTruncated };
  }

  function handleConfirmImport() {
    if (hasExistingData && !confirmingReplace) {
      setConfirmingReplace(true);
      return;
    }
    const built = buildSeriesFromMapping();
    if (built.series.length === 0 || built.series.every((s) => s.points.length === 0)) {
      // Never silently commit an empty result over existing data — surface it so the teacher can
      // fix the column mapping instead of just seeing their chart go blank with no explanation.
      setToastMsg(t('blocks.dataviz.csv.mapping.noValidRows'));
      return;
    }
    // Truncation info is passed up rather than toasted here — this dialog closes (unmounts) the
    // instant the parent handles onImport, so a toast set on this component's own state would never
    // get a chance to render. The parent shows it instead, since it stays mounted afterward.
    onImport(built.series, built.xAxisType, { seriesTruncated: built.seriesTruncated, pointsTruncated: built.pointsTruncated });
  }

  const previewRows = parsedRows.slice(firstRowIsHeaders ? 1 : 0, (firstRowIsHeaders ? 1 : 0) + 5);
  const headerRow = parsedRows[0] ?? [];

  return (
    <div
      role='presentation'
      className={`fixed inset-0 z-50 flex bg-ws-modal-overlay p-4 ${isMobile ? 'items-end' : 'items-center justify-center'}`}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={overlayRef}
        role='dialog'
        aria-modal='true'
        aria-label={t('blocks.dataviz.csv.importButton')}
        className={`flex w-full flex-col overflow-hidden border border-ws-border bg-ws-elevated shadow-2xl ${isMobile ? 'max-h-[85dvh] rounded-t-2xl' : 'max-w-4xl rounded-2xl'}`}
      >
        <div className='flex shrink-0 items-center justify-between border-b border-ws-border px-4 py-3'>
          <h2 className='text-sm font-semibold text-ws-text'>{t('blocks.dataviz.csv.importButton')}</h2>
          <IconButton aria-label={t('common.close')} onClick={onClose}>
            <X className='h-4 w-4' />
          </IconButton>
        </div>

        <div className='overflow-y-auto p-4'>
          {step === 'input' && (
            <div className='flex flex-col gap-3'>
              <div role='tablist' className='flex gap-1 border-b border-ws-border'>
                <button
                  ref={pasteTabRef}
                  type='button'
                  role='tab'
                  aria-selected={inputMode === 'paste'}
                  onClick={() => setInputMode('paste')}
                  className={`px-3 py-1.5 text-sm font-medium ${inputMode === 'paste' ? 'border-b-2 border-ws-primary text-ws-text' : 'text-ws-muted'}`}
                >
                  {t('blocks.dataviz.csv.pasteTab')}
                </button>
                <button
                  type='button'
                  role='tab'
                  aria-selected={inputMode === 'upload'}
                  onClick={() => setInputMode('upload')}
                  className={`px-3 py-1.5 text-sm font-medium ${inputMode === 'upload' ? 'border-b-2 border-ws-primary text-ws-text' : 'text-ws-muted'}`}
                >
                  {t('blocks.dataviz.csv.uploadTab')}
                </button>
              </div>

              {inputMode === 'paste' ? (
                <>
                  <textarea
                    value={pasteText}
                    onChange={(e) => setPasteText(e.target.value)}
                    placeholder={t('blocks.dataviz.csv.pastePlaceholder')}
                    rows={8}
                    className='w-full rounded-md border border-ws-border bg-ws-surface p-2 text-sm text-ws-text'
                  />
                  <button
                    type='button'
                    disabled={!pasteText.trim()}
                    onClick={() => parseCsvText(pasteText)}
                    className='self-end rounded-md bg-ws-primary px-3 py-2 text-sm font-medium text-ws-btn-on-primary hover:opacity-90 disabled:opacity-50'
                  >
                    {t('blocks.dataviz.csv.mapping.title')}
                  </button>
                </>
              ) : (
                <button
                  type='button'
                  onClick={() => fileInputRef.current?.click()}
                  className='flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-ws-border p-8 text-ws-muted hover:border-ws-primary hover:text-ws-primary'
                >
                  <Upload className='h-6 w-6' />
                  {t('blocks.dataviz.csv.uploadTab')}
                </button>
              )}
              <input ref={fileInputRef} type='file' accept='.csv,text/csv' onChange={handleFileSelect} className='hidden' />
            </div>
          )}

          {step === 'mapping' && (
            <div className='flex flex-col gap-3'>
              <CheckboxRow
                checked={firstRowIsHeaders}
                onToggle={() => handleFirstRowHeaderToggle(!firstRowIsHeaders)}
                label={t('blocks.dataviz.csv.mapping.firstRowIsHeader')}
              />

              <div className='overflow-x-auto'>
                {/* table-fixed + an explicit min-width per column — plain `w-full` on the <select>
                    only fills whatever width the browser's auto table-layout algorithm happens to
                    give its column, which shrinks well below what "X-axis"/"Y-axis" need once there
                    are more than a couple of columns. Guaranteeing a real minimum width per column
                    and letting the wrapper's overflow-x-auto handle any overflow (rather than
                    letting columns compress into truncation) is what actually keeps the dropdowns
                    fully readable. */}
                <table className='w-full table-fixed border-collapse text-sm' style={{ minWidth: `${Math.max(headerRow.length, 1) * 110}px` }}>
                  <caption className='sr-only'>{t('blocks.dataviz.csv.mapping.previewCaption')}</caption>
                  <thead>
                    <tr>
                      {headerRow.map((_, colIndex) => {
                        const type = columnTypes[colIndex] ?? 'text';
                        const role = columnRoles[colIndex] ?? 'ignore';
                        const isEmpty = type === 'empty';
                        // Two ghosting tiers, never conflated: heavily ghosted + italic + no
                        // dropdown means "this column cannot be chosen at all" (no data in it).
                        // Lightly ghosted (dropdown still fully live) means "chooseable, just not
                        // currently chosen" — a column set to Ignore is a normal, changeable state,
                        // not an unavailable one, so it must still read as clearly different from
                        // empty rather than nearly full-strength.
                        const ghostClass = isEmpty ? 'opacity-30' : role === 'ignore' ? 'opacity-50' : '';
                        return (
                          <th key={colIndex} className={`border-b border-ws-border px-2 py-1.5 text-left align-top transition-opacity ${ghostClass}`}>
                            {isEmpty ? (
                              <p className='px-1 py-1 text-center text-xs italic text-ws-muted'>{t('blocks.dataviz.csv.mapping.noData')}</p>
                            ) : (
                              <div className='flex flex-col items-center gap-1'>
                                <SelectMenu
                                  value={role}
                                  fullWidth
                                  onChange={v => handleRoleChange(colIndex, v as ColumnRole)}
                                  options={[
                                    { value: 'ignore', label: t('blocks.dataviz.csv.mapping.ignore') },
                                    { value: 'x',      label: t('blocks.dataviz.csv.mapping.xAxis') },
                                    // y needs numbers; a text column can be seen but not chosen.
                                    { value: 'y',      label: t('blocks.dataviz.csv.mapping.yAxis'), disabled: type !== 'numeric' },
                                  ]}
                                  ariaLabel={t('blocks.dataviz.csv.mapping.columnRoleLabel', { column: colIndex + 1 })}
                                  className='w-full' />
                                {/* Detected type is noted, not editable here — re-typing a column
                                    (e.g. a misdetected date) lives in the block editor's grid so it
                                    never requires deleting data and re-importing from scratch. */}
                                <p className='text-center text-[11px] text-ws-muted'>{t(`blocks.dataviz.csv.mapping.columnType.${type}`)}</p>
                              </div>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {row.map((cell, colIndex) => {
                          const type = columnTypes[colIndex] ?? 'text';
                          const role = columnRoles[colIndex] ?? 'ignore';
                          const isEmpty = type === 'empty';
                          const ghostClass = isEmpty ? 'opacity-30' : role === 'ignore' ? 'opacity-50' : '';
                          return (
                            <td key={colIndex} className={`border-b border-ws-border px-2 py-1 text-ws-text transition-opacity ${ghostClass} ${isEmpty ? 'italic' : ''}`}>
                              {cell}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {confirmingReplace && (
                <p className='rounded-md bg-ws-warning-bg px-3 py-2 text-sm text-ws-warning-fg'>
                  {t('blocks.dataviz.csv.mapping.replaceConfirm')}
                </p>
              )}

              <div className='flex justify-end gap-2'>
                <button type='button' onClick={() => setStep('input')} className='rounded-md border border-ws-border px-3 py-2 text-sm font-medium text-ws-text hover:bg-ws-hover'>
                  {t('common.back')}
                </button>
                <Button variant='primary' onClick={handleConfirmImport} className='text-ws-btn-on-primary'>
                  {t('blocks.dataviz.csv.mapping.confirmImport')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {toastMsg && <Toast message={toastMsg} type='error' onDismiss={() => setToastMsg('')} />}
    </div>
  );
}
